import React, { useEffect, useState } from "react";
import { db } from "@/lib/firebase";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Edit3, Trash2, Tag, DollarSign, FileText, MapPin } from "lucide-react";

const AdminLicenseCodes: React.FC = () => {
  const [codes, setCodes] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [newCode, setNewCode] = useState({ code: "", description: "", price: "", branches: [] as string[] });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editing, setEditing] = useState({ code: "", description: "", price: "", branches: [] as string[] });
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [codesSnap, branchesSnap] = await Promise.all([
          getDocs(collection(db, "licenseCodes")),
          getDocs(collection(db, "branches"))
        ]);
        
        setCodes(codesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setBranches(branchesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCode.code || !newCode.description || !newCode.price || newCode.branches.length === 0) return;
    await addDoc(collection(db, "licenseCodes"), { 
      ...newCode, 
      price: parseFloat(newCode.price),
      createdAt: new Date()
    });
    setNewCode({ code: "", description: "", price: "", branches: [] });
    setShowAddDialog(false);
    const snap = await getDocs(collection(db, "licenseCodes"));
    setCodes(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const handleEdit = (c: any) => {
    setEditingId(c.id);
    setEditing({ 
      code: c.code, 
      description: c.description, 
      price: c.price.toString(),
      branches: c.branches || []
    });
    setShowEditDialog(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingId) return;
    await updateDoc(doc(db, "licenseCodes", editingId), { 
      ...editing, 
      price: parseFloat(editing.price),
      updatedAt: new Date()
    });
    setEditingId(null);
    setEditing({ code: "", description: "", price: "", branches: [] });
    setShowEditDialog(false);
    const snap = await getDocs(collection(db, "licenseCodes"));
    setCodes(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const handleDelete = async (id: string) => {
    await deleteDoc(doc(db, "licenseCodes", id));
    setCodes(codes.filter(c => c.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">License Codes</h1>
          <p className="text-muted-foreground">
            Manage license codes for driving students
          </p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add License Code
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Add New License Code</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  placeholder="e.g., DL-001"
                  value={newCode.code}
                  onChange={e => setNewCode({ ...newCode, code: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  placeholder="e.g., Standard License Application"
                  value={newCode.description}
                  onChange={e => setNewCode({ ...newCode, description: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Branches *</Label>
                <div className="border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                  {branches.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No branches available</p>
                  ) : (
                    branches.map((branch) => (
                      <div key={branch.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`branch-${branch.id}`}
                          checked={newCode.branches.includes(branch.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewCode({ ...newCode, branches: [...newCode.branches, branch.id] });
                            } else {
                              setNewCode({ ...newCode, branches: newCode.branches.filter(id => id !== branch.id) });
                            }
                          }}
                          className="rounded"
                        />
                        <label htmlFor={`branch-${branch.id}`} className="text-sm cursor-pointer">
                          {branch.name}
                        </label>
                      </div>
                    ))
                  )}
                </div>
                {newCode.branches.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {newCode.branches.length} branch(es) selected
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price (₱)</Label>
                <Input
                  id="price"
                  type="number"
                  placeholder="0.00"
                  value={newCode.price}
                  onChange={e => setNewCode({ ...newCode, price: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Add Code
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* License Codes Grid */}
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="text-muted-foreground">Loading license codes...</div>
        </div>
      ) : codes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-32">
            <Tag className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No license codes yet</h3>
            <p className="text-muted-foreground text-center">
              Add your first license code to get started
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {codes.map(c => (
            <Card key={c.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      {c.code}
                    </CardTitle>
                  </div>
                  <div className="flex space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(c)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete License Code</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{c.code}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(c.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">{c.description}</p>
                  </div>
                  
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-muted-foreground">
                      {c.branches && c.branches.length > 0 ? (
                        <div className="space-y-1">
                          {c.branches.map((branchId: string) => {
                            const branch = branches.find(b => b.id === branchId);
                            return branch ? (
                              <span key={branchId} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1 mb-1">
                                {branch.name}
                              </span>
                            ) : null;
                          })}
                        </div>
                      ) : (
                        'No branches assigned'
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-lg font-semibold">₱{c.price?.toLocaleString() || '0'}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit License Code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-code">Code</Label>
              <Input
                id="edit-code"
                value={editing.code}
                onChange={e => setEditing({ ...editing, code: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Input
                id="edit-description"
                value={editing.description}
                onChange={e => setEditing({ ...editing, description: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Branches *</Label>
              <div className="border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                {branches.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No branches available</p>
                ) : (
                  branches.map((branch) => (
                    <div key={branch.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`edit-branch-${branch.id}`}
                        checked={editing.branches.includes(branch.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setEditing({ ...editing, branches: [...editing.branches, branch.id] });
                          } else {
                            setEditing({ ...editing, branches: editing.branches.filter(id => id !== branch.id) });
                          }
                        }}
                        className="rounded"
                      />
                      <label htmlFor={`edit-branch-${branch.id}`} className="text-sm cursor-pointer">
                        {branch.name}
                      </label>
                    </div>
                  ))
                )}
              </div>
              {editing.branches.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {editing.branches.length} branch(es) selected
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-price">Price (₱)</Label>
              <Input
                id="edit-price"
                type="number"
                value={editing.price}
                onChange={e => setEditing({ ...editing, price: e.target.value })}
                required
                min="0"
                step="0.01"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setShowEditDialog(false);
                  setEditingId(null);
                  setEditing({ code: "", description: "", price: "", branches: [] });
                }}
              >
                Cancel
              </Button>
              <Button type="submit">
                Update Code
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLicenseCodes;
